import React, { useRef, useEffect, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getBounds, getCenter } from "geolib";
import { useTheme } from "@mui/material";
import CarImage from "assets/img/car-icon.svg";
import { customColors } from "models/customColors";
import { useMapAuth } from "./useMapAuth";

const ZOOM_FACTOR_PADDING = 60;
const DEFAULT_ZOOM_FOR_ONE = 12;
const MARKER_SIZE = 36;
const RELOAD_DELAY_IN_MS = 500;

const defaultMarker = {
  coordinates: { lat: 29.749907, lon: -95.358421 },
};

const FALLBACK_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [
    {
      id: "osm",
      type: "raster",
      source: "osm",
    },
  ],
};

function createCarMarkerElement(bgColor) {
  const el = document.createElement("div");
  el.style.width = `${MARKER_SIZE}px`;
  el.style.height = `${MARKER_SIZE}px`;
  el.style.backgroundImage = `url(${CarImage})`;
  el.style.backgroundSize = "60%";
  el.style.backgroundRepeat = "no-repeat";
  el.style.backgroundPosition = "center";
  el.style.backgroundColor = bgColor;
  el.style.borderRadius = "50%";
  el.style.border = "2px solid white";
  el.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
  el.style.cursor = "pointer";
  return el;
}

export const ActiveRidesMap = ({
  markers = [],
  reloadSignal,
  setSelectedId,
}) => {
  const theme = useTheme();
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const mapMarkersRef = useRef([]);
  const authOptions = useMapAuth();
  const [isVisible, setIsVisible] = useState(false);

  const colors = customColors[theme.palette.mode];

  useEffect(() => {
    setIsVisible(false);
    const timeout = setTimeout(() => setIsVisible(true), RELOAD_DELAY_IN_MS);
    return () => clearTimeout(timeout);
  }, [reloadSignal]);

  useEffect(() => {
    if (!mapContainer.current || !isVisible) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    mapMarkersRef.current = [];

    const dataPoints = markers.length > 0 ? markers : [defaultMarker];
    const geoPoints = dataPoints.map(({ coordinates: { lat, lon } }) => ({
      latitude: lat,
      longitude: lon,
    }));

    const center = getCenter(geoPoints);

    const hasSafeStyle =
      authOptions?.styleJson &&
      Array.isArray(authOptions?.styleJson?.layers) &&
      authOptions?.styleJson?.layers?.length > 0 &&
      authOptions?.styleJson?.sources &&
      Object.keys(authOptions?.styleJson?.sources || {}).length > 0;

    const styleToUse = hasSafeStyle ? authOptions.styleJson : FALLBACK_STYLE;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: styleToUse,
      center: [center ? center.longitude : 0, center ? center.latitude : 0],
      zoom: markers.length <= 1 ? DEFAULT_ZOOM_FOR_ONE : 10,
      ...(authOptions || {}),
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    let didForceFallback = false;
    map.on("error", (event) => {
      if (didForceFallback) return;
      const msg = event?.error?.message || "";
      // Guard against malformed/private style descriptor runtime failures.
      if (msg.includes("source") || msg.includes("style") || msg.includes("esri")) {
        didForceFallback = true;
        try {
          map.setStyle(FALLBACK_STYLE);
        } catch (e) {
          console.error("Fallback style switch failed", e);
        }
      }
    });

    map.on("load", () => {
      const bounds = new maplibregl.LngLatBounds();

      markers.forEach(
        ({ id, title, subtitle, driverWorkStatus, coordinates: { lat, lon } }) => {
          bounds.extend([lon, lat]);

          const markerColor =
            driverWorkStatus === "On_Shift" ? colors.new : colors.greyText;

          const el = createCarMarkerElement(markerColor);
          el.addEventListener("click", () => {
            if (setSelectedId) {
              setTimeout(() => setSelectedId(id), 300);
            }
          });

          const popup = new maplibregl.Popup({ offset: 25 }).setText(
            `${title || ""} — ${subtitle || ""}`
          );

          const marker = new maplibregl.Marker({ element: el })
            .setLngLat([lon, lat])
            .setPopup(popup)
            .addTo(map);

          mapMarkersRef.current.push(marker);
        }
      );

      if (markers.length > 1 && !bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: ZOOM_FACTOR_PADDING });
      }
    });

    mapRef.current = map;

    return () => {
      mapMarkersRef.current.forEach((m) => m.remove());
      mapMarkersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOptions, markers, isVisible]);

  if (!isVisible) return null;

  return (
    <div
      ref={mapContainer}
      style={{ width: "100%", height: "100%", minHeight: 400 }}
    />
  );
};
